import { useEffect, useRef, useState } from 'react';
import { IPresenterProps } from './types';
import * as S from './styles';
import searchIcon from '../../assets/img/search.png';
import ItemPill from '../ItemPill/Container';
import { ICategory, ISearchBarData } from '../../state/api/types';
import { Search, X, ChevronLeft } from 'lucide-react';

const SearchBarPresenter = (props: IPresenterProps) => {
    const [businessInputFocus, setBusinessInputFocus] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [mobileModalOpen, setMobileModalOpen] = useState(false);
    const [mobileActiveStep, setMobileActiveStep] = useState<'main' | 'category'>('main');

    const BusinessInputRef = useRef<HTMLInputElement>(null);
    const MobileBusinessInputRef = useRef<HTMLInputElement>(null);

    // Handle mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Lock body scroll when mobile modal is open
    useEffect(() => {
        if (mobileModalOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
        };
    }, [mobileModalOpen]);

    // Focus input when step changes
    useEffect(() => {
        if (mobileActiveStep === 'category' && MobileBusinessInputRef.current) {
            setTimeout(() => MobileBusinessInputRef.current?.focus(), 100);
            props.searchBusinessCategories();
        }
    }, [mobileActiveStep]);

    const openMobileModal = () => {
        setMobileModalOpen(true);
        setMobileActiveStep('main');
    };

    const closeMobileModal = () => {
        setMobileModalOpen(false);
        setMobileActiveStep('main');
    };

    const getMobileSummaryText = () => {
        if (props.selectedCategories.length > 0) {
            return props.selectedCategories.map(c => c.name).join(', ');
        }
        return '¿Qué servicio buscás?';
    };

    // Mobile Search Modal
    const renderMobileModal = () => {
        if (!mobileModalOpen) return null;

        return (
            <S.MobileModalOverlay onClick={closeMobileModal}>
                <S.MobileModal onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <S.MobileModalHeader>
                        <S.MobileBackButton onClick={() => {
                            if (mobileActiveStep === 'main') {
                                closeMobileModal();
                            } else {
                                setMobileActiveStep('main');
                            }
                        }}>
                            {mobileActiveStep === 'main' ? <X size={24} /> : <ChevronLeft size={24} />}
                        </S.MobileBackButton>
                        <S.MobileModalTitle>
                            {mobileActiveStep === 'main' && 'Buscar'}
                            {mobileActiveStep === 'category' && 'Tratamientos'}
                        </S.MobileModalTitle>
                        <div style={{ width: 40 }} />
                    </S.MobileModalHeader>

                    <S.MobileModalContent>
                        {mobileActiveStep === 'main' && (
                            <>
                                {/* Category Card */}
                                <S.MobileSearchCard onClick={() => setMobileActiveStep('category')}>
                                    <S.MobileCardIcon>
                                        <Search size={20} />
                                    </S.MobileCardIcon>
                                    <S.MobileCardContent>
                                        <S.MobileCardLabel>Tratamientos</S.MobileCardLabel>
                                        <S.MobileCardValue hasValue={props.selectedCategories.length > 0}>
                                            {props.selectedCategories.length > 0
                                                ? props.selectedCategories.map(c => c.name).join(', ')
                                                : 'Buscar servicios...'
                                            }
                                        </S.MobileCardValue>
                                    </S.MobileCardContent>
                                    {props.selectedCategories.length > 0 && (
                                        <S.MobileCardClear onClick={(e) => {
                                            e.stopPropagation();
                                            props.selectedCategories.forEach(c => props.removeCategory(c));
                                        }}>
                                            <X size={16} />
                                        </S.MobileCardClear>
                                    )}
                                </S.MobileSearchCard>

                                {/* Buscar button */}
                                <S.MobileSearchButtonWrapper>
                                    <S.StyledLink to={{
                                        pathname: "/search",
                                        state: {
                                            searchData: props.formData as ISearchBarData,
                                            selectedCat: props.selectedCategories as ICategory[]
                                        }
                                    }}>
                                        <S.MobileSearchButton onClick={closeMobileModal}>
                                            <Search size={20} />
                                            Buscar
                                        </S.MobileSearchButton>
                                    </S.StyledLink>
                                </S.MobileSearchButtonWrapper>
                            </>
                        )}

                        {mobileActiveStep === 'category' && (
                            <S.MobileStepContent>
                                <S.MobileInputWrapper>
                                    <Search size={20} />
                                    <S.MobileInput
                                        ref={MobileBusinessInputRef}
                                        value={props.formData.business}
                                        placeholder="Buscar servicios..."
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            props.onChange(e, 'business');
                                            props.searchBusinessCategories();
                                        }}
                                    />
                                </S.MobileInputWrapper>

                                {props.selectedCategories.length > 0 && (
                                    <S.MobileSelectedPills>
                                        {props.selectedCategories.map(category => (
                                            <S.MobilePill key={category.id}>
                                                {category.name}
                                                <S.MobilePillRemove onClick={() => props.removeCategory(category)}>
                                                    <X size={14} />
                                                </S.MobilePillRemove>
                                            </S.MobilePill>
                                        ))}
                                    </S.MobileSelectedPills>
                                )}

                                <S.MobileResultsList>
                                    <S.MobileResultsTitle>Tratamientos</S.MobileResultsTitle>
                                    {props.searchResults.map(category => (
                                        <S.MobileResultItem
                                            key={category.id}
                                            onClick={() => {
                                                if (props.selectedCategories.length > 0) {
                                                    props.selectedCategories.forEach(c => props.removeCategory(c));
                                                }
                                                props.addCategory(category);
                                                setMobileActiveStep('main');
                                            }}
                                        >
                                            {category.name}
                                        </S.MobileResultItem>
                                    ))}
                                </S.MobileResultsList>
                            </S.MobileStepContent>
                        )}
                    </S.MobileModalContent>
                </S.MobileModal>
            </S.MobileModalOverlay>
        );
    };

    // Mobile collapsed pill (Airbnb style)
    if (isMobile) {
        return (
            <>
                <S.MobileSearchPill onClick={openMobileModal}>
                    <S.MobileSearchPillIcon>
                        <Search size={18} />
                    </S.MobileSearchPillIcon>
                    <S.MobileSearchPillText>
                        <S.MobileSearchPillTitle>
                            {getMobileSummaryText()}
                        </S.MobileSearchPillTitle>
                        <S.MobileSearchPillSubtitle>
                            Cualquier fecha · Cerca de ti
                        </S.MobileSearchPillSubtitle>
                    </S.MobileSearchPillText>
                    <S.MobileSearchPillButton>
                        <Search size={16} />
                        Buscar
                    </S.MobileSearchPillButton>
                </S.MobileSearchPill>
                {renderMobileModal()}
            </>
        );
    }

    // Desktop version
    return (
        <S.Container>
            {/* CATEGORIES SUGGESTIONS BOX */}
            {
                props.isCategoriesBoxVisible &&
                <S.ResultsBox>
                    <S.SuggestBoxTitle>Tratamientos</S.SuggestBoxTitle>
                    <S.ResultsContainer>
                        {props.searchResults.map(category => (
                            <S.ResultsItem onClick={() => { props.addCategory(category) }} key={category.id} onMouseDown={(e) => e.preventDefault()}>
                                <p>{category.name}</p>
                            </S.ResultsItem>
                        ))}
                    </S.ResultsContainer>
                </S.ResultsBox>
            }
            {/* CATEGORY */}
            <S.Section onClick={() => { BusinessInputRef.current?.focus(); }} focus={businessInputFocus} flex={1.3} scrollX>
                <S.Row>
                    <S.IconWrapper>
                        <Search size={20} />
                    </S.IconWrapper>
                    <div style={{ flex: 1 }}>
                        <S.Label>Tratamientos</S.Label>
                        <S.InputCategoriesContainer>
                        {props.selectedCategories.map(category => (
                            <div style={{ marginRight: 3 }} key={category.id}>
                                <ItemPill service={category} removeService={() => props.removeCategory(category)} />
                            </div>
                        ))}
                        <S.Input
                            onFocus={(e) => { setBusinessInputFocus(true); props.toggleCategoriesBoxVisibility(true); props.searchBusinessCategories() }}
                            onBlur={(e) => {
                                setBusinessInputFocus(false);
                                props.toggleCategoriesBoxVisibility(false);
                            }}
                            value={props.formData.business}
                            placeholder={props.selectedCategories.length === 0 ? 'Buscar servicios...' : ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => props.onChange(e, 'business')}
                            ref={BusinessInputRef}>
                        </S.Input>
                    </S.InputCategoriesContainer>
                    </div>
                </S.Row>
            </S.Section>
            <S.StyledLink to={{
                pathname: "/search",
                state: {
                    searchData: props.formData as ISearchBarData,
                    selectedCat: props.selectedCategories as ICategory[]
                }
            }}>
                <S.SearchBtnContainer>
                    <S.SearchBtn isBtnDisabled={props.isBtnDisabled}>
                        <img src={searchIcon} alt="search" />
                        <div>
                            <p>Buscar</p>
                        </div>
                    </S.SearchBtn>
                </S.SearchBtnContainer>
            </S.StyledLink>
        </S.Container>
    )
};

export default SearchBarPresenter;
